// components/dashboard/widgets/AnaliseAtrasos.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, List, Tag, Progress, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useDashboardData } from '../../../hooks/useDashboardData';

const AnaliseAtrasos = ({ endpoint, filtros, formato }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando análise de atrasos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '6px',
          color: '#a8071a'
        }}
      >
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar análise de atrasos
        </p>
        <small style={{ color: '#cf1322' }}>{error?.message || String(error)}</small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#52c41a' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h4 style={{ color: '#52c41a', margin: 0 }}>Nenhum atraso detectado!</h4>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0' }}>
          Todos os projetos estão dentro do prazo.
        </p>
      </div>
    );
  }

  // --- Helpers ---------------------------------------------------------------

  const getGravidadeAtraso = (diasAtraso) => {
    if (diasAtraso >= 90) return 'critico';
    if (diasAtraso >= 60) return 'alto';
    if (diasAtraso >= 30) return 'medio';
    if (diasAtraso >= 15) return 'baixo';
    return 'leve';
  };

  const getCor = (gravidade) => {
    const cores = {
      critico: '#f5222d',
      alto: '#fa541c',
      medio: '#fa8c16',
      baixo: '#fadb14',
      leve: '#52c41a'
    };
    return cores[gravidade] || '#8c8c8c';
  };

  const getIcone = (gravidade) => {
    if (gravidade === 'critico') return <ExclamationCircleOutlined />;
    if (gravidade === 'alto' || gravidade === 'medio') return <WarningOutlined />;
    return <ClockCircleOutlined />;
  };

  const formatarData = (data) => {
    if (!data) return 'Não informada';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  // --- Processamento ---------------------------------------------------------

  const dadosProcessados = dados
    .map((item) => {
      const diasAtraso =
        parseInt(item.dias_atraso, 10) || parseInt(item.dias_atrasados, 10) || 0;
      const dataLimite = item.data_limite || item.data_prazo || null;
      const estudanteNome = item.estudante || item.nome_estudante || 'Não informado';
      const projeto =
        item.projeto || item.titulo_projeto || item.titulo_proposta || 'Projeto sem título';

      return {
        estudante: estudanteNome,
        projeto: projeto.length > 50 ? `${projeto.substring(0, 50)}...` : projeto,
        projeto_completo: projeto,
        dias_atraso: diasAtraso,
        data_limite: dataLimite,
        curso: item.curso || 'Não informado',
        tipo_atraso: item.tipo_atraso || 'Geral',
        gravidade: getGravidadeAtraso(diasAtraso),
        fase: item.fase || item.etapa || 'Não especificada'
      };
    })
    .filter((item) => item.dias_atraso > 0)
    .sort((a, b) => b.dias_atraso - a.dias_atraso);

  const estatisticas = {
    total: dadosProcessados.length,
    criticos: dadosProcessados.filter((i) => i.gravidade === 'critico').length,
    altos: dadosProcessados.filter((i) => i.gravidade === 'alto').length,
    medios: dadosProcessados.filter((i) => i.gravidade === 'medio').length,
    mediaAtraso:
      dadosProcessados.length > 0
        ? dadosProcessados.reduce((sum, i) => sum + i.dias_atraso, 0) /
          dadosProcessados.length
        : 0,
    maiorAtraso:
      dadosProcessados.length > 0
        ? Math.max(...dadosProcessados.map((i) => i.dias_atraso))
        : 0
  };

  const dadosPizza = [
    { name: 'Crítico', value: estatisticas.criticos, color: '#f5222d' },
    { name: 'Alto', value: estatisticas.altos, color: '#fa541c' },
    { name: 'Médio', value: estatisticas.medios, color: '#fa8c16' },
    {
      name: 'Baixo',
      value: dadosProcessados.filter((i) => i.gravidade === 'baixo').length,
      color: '#fadb14'
    },
    {
      name: 'Leve',
      value: dadosProcessados.filter((i) => i.gravidade === 'leve').length,
      color: '#52c41a'
    }
  ].filter((i) => i.value > 0);

  // --- Tooltip com PropTypes para o lint ------------------------------------

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '200px'
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>👤 {data.estudante}</p>
          <div style={{ fontSize: 12 }}>
            <p style={{ margin: '4px 0', color: '#1890ff' }}>📚 {data.projeto}</p>
            <p style={{ margin: '4px 0', color: '#666' }}>🏫 Curso: {data.curso}</p>
            <p style={{ margin: '4px 0', color: getCor(data.gravidade) }}>
              ⏰ Atraso: <strong>{data.dias_atraso} dias</strong>
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>📋 Fase: {data.fase}</p>
            <div
              style={{
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: `${getCor(data.gravidade)}20`,
                color: getCor(data.gravidade),
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 'bold'
              }}
            >
              {data.gravidade.toUpperCase()}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
      PropTypes.shape({
        payload: PropTypes.shape({
          estudante: PropTypes.string,
          projeto: PropTypes.string,
          curso: PropTypes.string,
          gravidade: PropTypes.string,
          dias_atraso: PropTypes.number,
          fase: PropTypes.string
        })
      })
    )
  };

  // --- Renderizações por formato --------------------------------------------

  if (formato === 'lista') {
    return (
      <div style={{ height: 350, overflowY: 'auto' }}>
        {estatisticas.criticos > 0 && (
          <Alert
            message={`${estatisticas.criticos} projetos com atraso crítico`}
            description="Ação imediata necessária para projetos com mais de 90 dias de atraso."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 8,
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 11
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#f5222d' }}>{estatisticas.total}</div>
            <div style={{ color: '#666' }}>Total Atrasos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa541c' }}>{estatisticas.criticos}</div>
            <div style={{ color: '#666' }}>Críticos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
              {estatisticas.mediaAtraso.toFixed(0)}
            </div>
            <div style={{ color: '#666' }}>Média (dias)</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>{estatisticas.maiorAtraso}</div>
            <div style={{ color: '#666' }}>Maior Atraso</div>
          </div>
        </div>

        <List
          dataSource={dadosProcessados.slice(0, 15)}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '12px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: item.gravidade === 'critico' ? '#fff2f0' : 'transparent'
              }}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: getCor(item.gravidade),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 16
                    }}
                  >
                    {getIcone(item.gravidade)}
                  </div>
                }
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', fontSize: 13 }}>{item.estudante}</span>
                    <Tag color={getCor(item.gravidade)} style={{ fontSize: 10 }}>
                      {item.dias_atraso} DIAS
                    </Tag>
                    <Tag color="default" style={{ fontSize: 10 }}>
                      {item.gravidade.toUpperCase()}
                    </Tag>
                  </div>
                }
                description={
                  <div style={{ fontSize: 12 }}>
                    <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: 4 }}>
                      📚 {item.projeto}
                    </div>
                    <div style={{ color: '#666', marginBottom: 4 }}>
                      🏫 {item.curso} • 📋 {item.fase}
                    </div>
                    <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                      📅 Limite: {formatarData(item.data_limite)}
                    </div>
                  </div>
                }
              />
              <div style={{ textAlign: 'right' }}>
                <Progress
                  type="circle"
                  percent={
                    estatisticas.maiorAtraso > 0
                      ? Math.min((item.dias_atraso / estatisticas.maiorAtraso) * 100, 100)
                      : 0
                  }
                  width={40}
                  strokeColor={getCor(item.gravidade)}
                  format={() => item.dias_atraso}
                />
              </div>
            </List.Item>
          )}
        />
      </div>
    );
  }

  if (formato === 'pizza') {
    return (
      <div style={{ height: 350 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 12
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#f5222d' }}>{estatisticas.total}</div>
            <div style={{ color: '#666' }}>Total</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa541c' }}>{estatisticas.criticos}</div>
            <div style={{ color: '#666' }}>Críticos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
              {estatisticas.mediaAtraso.toFixed(0)}
            </div>
            <div style={{ color: '#666' }}>Média</div>
          </div>
        </div>

        <div style={{ width: '100%', height: 'calc(350px - 80px)' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dadosPizza}
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
                paddingAngle={2}
                dataKey="value"
              >
                {dadosPizza.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, `${name} (${value} projetos)`]} />
              <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Gráfico de barras (padrão)
  return (
    <div style={{ height: 350 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: 16,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          fontSize: 12
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#f5222d' }}>{estatisticas.total}</div>
          <div style={{ color: '#666' }}>Total Atrasos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa541c' }}>{estatisticas.criticos}</div>
          <div style={{ color: '#666' }}>Críticos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
            {estatisticas.mediaAtraso.toFixed(0)}
          </div>
          <div style={{ color: '#666' }}>Média (dias)</div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(350px - 110px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dadosProcessados.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="estudante"
              tick={{ fontSize: 10 }}
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              label={{ value: 'Dias de Atraso', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="dias_atraso" name="Dias de Atraso" radius={[4, 4, 0, 0]}>
              {dadosProcessados.slice(0, 10).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getCor(entry.gravidade)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          Crítico (≥90d)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fa541c' }} />
          Alto (60-89d)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fa8c16' }} />
          Médio (30-59d)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fadb14' }} />
          Baixo (15-29d)
        </span>
      </div>
    </div>
  );
};

AnaliseAtrasos.displayName = 'AnaliseAtrasos';
AnaliseAtrasos.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  formato: PropTypes.oneOf(['grafico', 'lista', 'pizza'])
};

AnaliseAtrasos.defaultProps = {
  filtros: {},
  formato: 'grafico'
};

export default AnaliseAtrasos;
